import React, { useEffect, useMemo, useState } from "react";
import { ArrowLeft, Search, ShieldCheck, Check, Minus, Lock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { PageHeader } from "../lib/ui";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

const coverage = (permset, rk, actions) => {
  const has = actions.filter((a) => permset.has(`${rk}.${a}`)).length;
  return { has, total: actions.length };
};

const CellFull = () => <span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-emerald-500 text-white"><Check className="w-4 h-4" /></span>;
const CellNone = () => <span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-gray-100 text-gray-300"><Minus className="w-4 h-4" /></span>;
const CellPartial = ({ has, total }) => <span className="inline-flex items-center justify-center min-w-[24px] h-6 px-1 rounded-md bg-amber-400 text-cocoa text-[11px] font-semibold">{has}/{total}</span>;

export default function PermissionMatrix() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [moduleKey, setModuleKey] = useState("all");
  const [hidden, setHidden] = useState({});

  useEffect(() => { api.get("/roles/matrix").then((r) => setData(r.data)).catch(() => {}); }, []);

  const roleSets = useMemo(() => {
    const m = {};
    (data?.roles || []).forEach((r) => { m[r.key] = new Set(r.perms); });
    return m;
  }, [data]);

  if (!data) return <div className="p-6 text-wmuted" data-testid="matrix-loading">Memuat matriks izin…</div>;

  const roles = data.roles.filter((r) => !hidden[r.key]);
  const modules = data.modules.filter((m) => moduleKey === "all" || m.key === moduleKey);
  const ql = q.trim().toLowerCase();

  return (
    <div data-testid="permission-matrix-page">
      <PageHeader title="Matriks Izin — Peran vs Akses" subtitle="Audit cepat siapa bisa apa. Penuh = semua aksi menu; Sebagian = sebagian aksi. Arahkan kursor ke sel untuk rincian.">
        <Button variant="outline" className="border-taupe text-cocoa" onClick={() => navigate("/roles")} data-testid="matrix-back"><ArrowLeft className="w-4 h-4 mr-1.5" />Kembali</Button>
      </PageHeader>

      <div className="rounded-xl border border-taupe/30 bg-white p-3 mb-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-wmuted" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari menu / resource…" className="pl-8 w-56" data-testid="matrix-search" />
          </div>
          <select value={moduleKey} onChange={(e) => setModuleKey(e.target.value)} className="h-9 rounded-md border border-taupe/40 bg-white px-2 text-sm text-cocoa" data-testid="matrix-module-filter">
            <option value="all">Semua Modul</option>
            {data.modules.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
          </select>
          <div className="flex items-center gap-3 text-xs text-wmuted ml-auto">
            <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-emerald-500 inline-block" />Penuh</span>
            <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-amber-400 inline-block" />Sebagian</span>
            <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-gray-200 inline-block" />Tidak ada</span>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap gap-2" data-testid="matrix-role-toggles">
          <span className="text-xs text-wmuted self-center">Tampilkan peran:</span>
          {data.roles.map((r) => (
            <button key={r.key} onClick={() => setHidden((h) => ({ ...h, [r.key]: !h[r.key] }))}
              className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${hidden[r.key] ? "bg-white text-wmuted border-taupe/30" : "bg-cocoa text-ivory border-cocoa"}`}
              data-testid={`matrix-role-toggle-${r.key}`}>
              {r.label}{r.is_owner ? " ★" : ""}
            </button>
          ))}
        </div>
      </div>

      <div className="overflow-auto rounded-xl border border-taupe/30 bg-white" style={{ maxHeight: "72vh" }}>
        <table className="text-sm border-collapse" data-testid="matrix-table">
          <thead className="sticky top-0 z-20">
            <tr>
              <th className="sticky left-0 z-30 bg-cocoa text-ivory text-left px-3 py-2 min-w-[220px]">Menu / Resource</th>
              {roles.map((r) => (
                <th key={r.key} className="bg-cocoa text-ivory px-2 py-2 text-center whitespace-nowrap min-w-[96px]" title={`${r.label} • ${r.user_count} pengguna`}>
                  <div className="flex flex-col items-center leading-tight">
                    <span className="inline-flex items-center gap-1">{r.is_owner ? <Lock className="w-3 h-3" /> : <ShieldCheck className="w-3 h-3" />}{r.label}</span>
                    <span className="text-[10px] opacity-70">{r.user_count} user</span>
                  </div>
                </th>
              ))}
              {!roles.length && <th className="bg-cocoa text-ivory px-3 py-2 text-xs font-normal">Pilih minimal satu peran</th>}
            </tr>
          </thead>
          <tbody>
            {modules.map((m) => {
              const resources = m.resources.filter((res) => !ql || res.label.toLowerCase().includes(ql) || m.label.toLowerCase().includes(ql));
              if (!resources.length) return null;
              return (
                <React.Fragment key={m.key}>
                  <tr><td colSpan={roles.length + 1} className="bg-taupe/15 text-cocoa font-semibold px-3 py-1.5 sticky left-0">{m.label}</td></tr>
                  {resources.map((res) => (
                    <tr key={res.key} className="border-t border-taupe/15 hover:bg-cream/40" data-testid={`matrix-row-${res.key}`}>
                      <td className="sticky left-0 z-10 bg-white px-3 py-1.5 text-cocoa">{res.label}<span className="block text-[10px] text-wmuted">{res.actions.length} aksi</span></td>
                      {roles.map((r) => {
                        const { has, total } = coverage(roleSets[r.key], res.key, res.actions);
                        const tip = res.actions.map((a) => `${(data.action_meta[a]?.label) || a}: ${roleSets[r.key].has(`${res.key}.${a}`) ? "✓" : "✗"}`).join("\n");
                        return (
                          <td key={r.key} className="text-center px-1 py-1" title={tip} data-testid={`matrix-cell-${res.key}-${r.key}`}>
                            {has === 0 ? <CellNone /> : has >= total ? <CellFull /> : <CellPartial has={has} total={total} />}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </React.Fragment>
              );
            })}
            {moduleKey === "all" && data.sensitive_assets.map((grp) => {
              const items = grp.items.filter((it) => !ql || it.label.toLowerCase().includes(ql) || grp.group.toLowerCase().includes(ql));
              if (!items.length) return null;
              return (
                <React.Fragment key={grp.group}>
                  <tr><td colSpan={roles.length + 1} className="bg-rose-100/60 text-rose-800 font-semibold px-3 py-1.5 sticky left-0">Sensitif · {grp.group}</td></tr>
                  {items.map((it) => (
                    <tr key={it.key} className="border-t border-taupe/15 hover:bg-cream/40" data-testid={`matrix-row-${it.key}`}>
                      <td className="sticky left-0 z-10 bg-white px-3 py-1.5 text-cocoa">{it.label}</td>
                      {roles.map((r) => (
                        <td key={r.key} className="text-center px-1 py-1" data-testid={`matrix-cell-${it.key}-${r.key}`}>
                          {roleSets[r.key].has(it.key) ? <CellFull /> : <CellNone />}
                        </td>
                      ))}
                    </tr>
                  ))}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
